import datadog from '@nerdwallet/datadog';
import {
  OVERRIDE_LINK_NOT_WHITELISTED_MSG,
  OVERRIDE_LINK_MALFORMED_MSG,
  OverrideError, // eslint-disable-next-line import/extensions -- directive added automatically by Shepherd migration
} from './errors.js';
import PRODUCT_TYPES from '../config/marketplace-product-types';

export const VALID_OVERRIDE_PRODUCT_TYPES = [
  PRODUCT_TYPES.EDUCATION_LOANS,
  PRODUCT_TYPES.MORTGAGES,
  PRODUCT_TYPES.PERSONAL_LOANS,
  PRODUCT_TYPES.INSURANCE,
];

/**
 * Helper function to decode a base64 encoded override http url
 */
export const decodeUrl = (productType, url) => {
  if (!url) {
    return '';
  }

  let result = '';
  let encoding = 'unknown';

  try {
    const unquotedUrl = decodeURIComponent(url);

    // support the old behaviour by testing if the unquotedUrl begins with https:// or http://
    // and only base64 decoding if it does not.
    //
    // TODO: remove this once nobody is using base64 encoded overrides.

    const re = /^http[s]*:\/\//;

    if (re.test(unquotedUrl)) {
      result = unquotedUrl;
      encoding = 'uri_encoded';
    } else {
      result = Buffer.from(unquotedUrl, 'base64').toString();
      encoding = 'base64_encoded';

      if (!result.startsWith('http')) {
        datadog.increment('interstitial_client.override_link.decode.error', 1, {
          tags: { productType },
        });
        throw new OverrideError(
          `${OVERRIDE_LINK_MALFORMED_MSG}: URL ${result} is not a http or https URL`
        );
      }
    }
  } catch (e) {
    datadog.increment('interstitial_client.override_link.decode.error', 1, {
      tags: { product_type: productType },
    });
    throw new OverrideError(
      `${OVERRIDE_LINK_MALFORMED_MSG}: invalid encoded URL ${url}`
    );
  }

  datadog.increment('interstitial_client.override_link.decode.success', 1, {
    tags: { product_type: productType, encoding },
  });

  return result;
};

/**
 * Validates override urls for Credit Cards
 */
const validateCreditCardsOverride = (override) => {
  let url = {};
  try {
    url = new URL(override);
  } catch (e) {
    throw new OverrideError(
      `${OVERRIDE_LINK_MALFORMED_MSG}: invalid URL ${override}`
    );
  }

  if (
    (url.hostname === 'goto.capitalone.com' &&
      url.pathname.match(/\/\w+\/\d+\/\d+\/\d+/g)) ||
    // this was a hack added for the Cards Cardalyzer application because it gets dynamic redirect URLs
    url.hostname === 'self-lender.3qcw.net'
  ) {
    datadog.increment('interstitial_client.override_link.credit_cards.valid');
    return true;
  }
  datadog.increment('interstitial_client.override_link.credit_cards.invalid');
  throw new OverrideError(
    `${OVERRIDE_LINK_NOT_WHITELISTED_MSG}: hostname ${url.hostname} or pathname ${url.pathname} not valid for CreditCardsOverride`
  );
};

/**
 * Validates override urls for product types allowed in VALID_OVERRIDE_PRODUCT_TYPES
 */
const validateOverride = (override, whiteList = []) => {
  let url = {};
  try {
    url = new URL(override);
  } catch (e) {
    throw new OverrideError(
      `${OVERRIDE_LINK_MALFORMED_MSG}: invalid URL ${override}`
    );
  }

  // eslint-disable-next-line no-restricted-syntax -- directive added automatically by Shepherd migration
  for (const prefix of whiteList) {
    if (url.origin === prefix) {
      datadog.increment('interstitial_client.override_link.loans.valid');
      return true;
    }
  }
  datadog.increment('interstitial_client.override_link.loans.invalid');
  throw new OverrideError(
    `${OVERRIDE_LINK_NOT_WHITELISTED_MSG}: origin ${url.origin} does not match any whitelist entry exactly`
  );
};

/**
 * Helper function that returns true if a decoded override url is valid
 */
export const validateOverrideUrl = (productType, override, whiteList = []) => {
  if (VALID_OVERRIDE_PRODUCT_TYPES.includes(productType)) {
    return validateOverride(override, whiteList);
  }
  if (productType === PRODUCT_TYPES.CREDIT_CARDS) {
    return validateCreditCardsOverride(override);
  }
  return false;
};
